// @ts-nocheck
/*
 * Port of views/dashboard/OverallDemoConversionAnalytics.js.
 * APIs: API_DEMO_OVERALL_ANALYTICS + API_COMPANY_LIST + API_COMPANY_DEMO_SUMMARY.
 * Byte-identical:
 *   - Default range: month-start → today; 3-month lookback cap
 *   - Company drill-down via :id substitution
 *   - 60-second refetch cooldown
 *   - CSV export headers preserved verbatim (both overall + per-company)
 * Sections preserved (as chart panels below):
 *   1. Converted vs Active vs Expired vs Deleted Demo (donut)
 *   2. Incoming vs Outgoing (donut)
 *   3. Entities Created (bar)
 *   4. Conversions Over Time (bar)
 *   5. Daily Calls (area/bar)
 *   6. Credits (horizontal bar)
 *   7. Recent Demo Conversions (table)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, RefreshCw } from 'lucide-react';
import Chart from 'react-apexcharts';
import { getApiCall } from '@/service/httpCall';
import { API_DEMO_OVERALL_ANALYTICS, API_COMPANY_LIST, API_COMPANY_DEMO_SUMMARY } from '@/service/url';
import { exportToCSV } from '@/utilities/helpers';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import {
  SUCCESS_COLOR,
  VIOLET_COLOR,
  FAILURE_COLOR,
  NEUTRAL_COLOR,
  INCOMING_COLOR,
  OUTGOING_COLOR,
} from '../chart-palette';

const REFETCH_COOLDOWN_MS = 60 * 1000;
const pad2 = (n) => String(n).padStart(2, '0');
const fmtYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const getDefaultDates = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  return {
    start: fmtYmd(firstOfMonth),
    end: fmtYmd(today),
    minFrom: fmtYmd(threeMonthsAgo),
    today: fmtYmd(today),
  };
};

const fmtTalkTime = (secs) => {
  const s = Number(secs) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m}m ${sec}s`;
};

function StatCard({ label, value, color = '#4f6edb' }) {
  return (
    <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]" style={{ borderLeftColor: color, borderLeftWidth: 4 }}>
      <p className="mb-1 text-xs text-muted-foreground">{label}</p>
      <h5 className="text-base font-bold text-foreground">{value}</h5>
    </div>
  );
}

function ChartPanel({ title, children }) {
  return (
    <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <h6 className="mb-2 text-sm font-bold text-foreground">{title}</h6>
      {children}
    </div>
  );
}

export default function OverallDemoConversionAnalytics() {
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);
  const [data, setData] = useState(null);
  const dateBounds = getDefaultDates();
  const [startDate, setStartDate] = usePersistedState('dashboard/OverallDemoConversion:startDate', dateBounds.start);
  const [endDate, setEndDate] = usePersistedState('dashboard/OverallDemoConversion:endDate', dateBounds.end);
  const [demoCompanies, setDemoCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = usePersistedState('dashboard/OverallDemoConversion:companyId', '');
  const [companyDetail, setCompanyDetail] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);

  const buildUrl = useCallback(() => {
    let url = API_DEMO_OVERALL_ANALYTICS;
    const qs = [];
    if (startDate) qs.push(`startDate=${startDate}`);
    if (endDate) qs.push(`endDate=${endDate}`);
    if (qs.length) url += `?${qs.join('&')}`;
    return url;
  }, [startDate, endDate]);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await getApiCall(buildUrl(), {});
      setData(resp || null);
      if (Array.isArray(resp?.companies)) setDemoCompanies(resp.companies);
      setLastFetchedAt(Date.now());
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to load overall demo analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  const fetchDemoCompanies = useCallback(async () => {
    try {
      const resp = await getApiCall(`${API_COMPANY_LIST}?queryData=demo`, {});
      setDemoCompanies((prev) => (prev?.length ? prev : Array.isArray(resp) ? resp : []));
    } catch (err) {}
  }, []);

  const fetchCompanyDetail = useCallback(
    async (companyId, fromDate = startDate, toDate = endDate) => {
      if (!companyId) { setCompanyDetail(null); return; }
      try {
        setCompanyLoading(true);
        let url = API_COMPANY_DEMO_SUMMARY.replace(':id', companyId);
        const qs = [];
        if (fromDate) qs.push(`startDate=${fromDate}`);
        if (toDate) qs.push(`endDate=${toDate}`);
        if (qs.length) url += `?${qs.join('&')}`;
        const resp = await getApiCall(url, {});
        setCompanyDetail(resp || null);
      } catch (err) {
        toast.error(friendlyApiError(err) || 'Failed to load company demo analytics');
        setCompanyDetail(null);
      } finally {
        setCompanyLoading(false);
      }
    },
    [startDate, endDate],
  );

  useEffect(() => {
    fetchSummary();
    fetchDemoCompanies();

  }, []);

  useEffect(() => {
    fetchCompanyDetail(selectedCompanyId);

  }, [selectedCompanyId]);

  const handleApply = () => {
    fetchSummary();
    if (selectedCompanyId) fetchCompanyDetail(selectedCompanyId);
  };

  const handleRefetch = async () => {
    const elapsed = Date.now() - lastFetchedAt;
    if (elapsed < REFETCH_COOLDOWN_MS) {
      const wait = Math.ceil((REFETCH_COOLDOWN_MS - elapsed) / 1000);
      toast.info(`Please wait ${wait}s before refreshing again`);
      return;
    }
    try {
      setRefetching(true);
      const resp = await getApiCall(buildUrl(), {});
      setData(resp || null);
      if (Array.isArray(resp?.companies)) setDemoCompanies(resp.companies);
      setLastFetchedAt(Date.now());
      if (selectedCompanyId) await fetchCompanyDetail(selectedCompanyId);
    } catch (err) {
      toast.error('Error refreshing overall demo analytics');
    } finally {
      setRefetching(false);
    }
  };

  const handleReset = () => {
    const def = getDefaultDates();
    setStartDate(def.start);
    setEndDate(def.end);
    setTimeout(() => {
      fetchSummary();
      if (selectedCompanyId) fetchCompanyDetail(selectedCompanyId, def.start, def.end);
    }, 0);
  };

  const o = data?.overview || {};
  const e = data?.entities || {};
  const c = data?.calls || {};
  const cr = data?.credits || {};
  const daily = data?.dailyCalls || [];
  const conv = data?.conversionsByMonth || [];
  const recent = data?.recentConversions || [];

  const companyOptions = useMemo(() => {
    const byId = new Map();
    (demoCompanies || []).forEach((co) => {
      if (!co?.id) return;
      byId.set(Number(co.id), {
        id: Number(co.id),
        name: co.name || `Company #${co.id}`,
        converted: !!co.demo_converted_at || !!co.converted,
        deleted: co.is_deleted === true || co.deleted === true,
        deleted_at: co.deleted_at || null,
      });
    });
    (recent || []).forEach((r) => {
      if (!r?.id || byId.has(Number(r.id))) return;
      byId.set(Number(r.id), {
        id: Number(r.id),
        name: r.name || `Company #${r.id}`,
        converted: true,
        deleted: false,
        deleted_at: null,
      });
    });
    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [demoCompanies, recent]);

  const selectedCompanyMeta =
    companyOptions.find((co) => String(co.id) === String(selectedCompanyId)) || null;
  const selectedCompanyName = selectedCompanyMeta
    ? `${selectedCompanyMeta.name}${selectedCompanyMeta.deleted ? ' (Deleted)' : selectedCompanyMeta.converted ? ' (converted)' : ''}`
    : '';

  const cd = companyDetail || null;
  const cdOverview = cd?.overview || {};
  const cdCalls = cd?.calls || {};
  const cdCredits = cd?.credits || {};
  const cdEntities = cd?.entities || {};
  const cdDaily = Array.isArray(cd?.dailyCalls) ? cd.dailyCalls : [];

  const handleExport = () => {
    if (selectedCompanyId) {
      if (!companyDetail) return;
      const co = companyDetail.overview || {};
      const cc = companyDetail.calls || {};
      const ccr = companyDetail.credits || {};
      const ce = companyDetail.entities || {};
      const companyRows = [
        { metric: 'Company', value: companyDetail.company?.name || selectedCompanyName },
        { metric: 'Account Age (days)', value: co.account_age_days },
        { metric: 'Days Used', value: co.days_used },
        { metric: 'Recharges', value: co.recharge_count },
        { metric: 'Converted', value: co.converted ? 'Yes' : 'No' },
        { metric: 'Conversion Date', value: co.conversion_date || '-' },
        { metric: 'Deleted', value: co.deleted ? 'Yes' : 'No' },
        { metric: 'Deleted At', value: co.deleted_at || '-' },
        { metric: 'Agents', value: ce.agents },
        { metric: 'Team Leads', value: ce.team_leads },
        { metric: 'Call Centers', value: ce.call_centres },
        { metric: 'Nested Companies', value: ce.nested_companies },
        { metric: 'Total Calls', value: cc.total },
        { metric: 'Incoming Calls', value: cc.incoming },
        { metric: 'Outgoing Calls', value: cc.outgoing },
        { metric: 'Answered Calls', value: cc.answered },
        { metric: 'Failed Calls', value: cc.failed },
        { metric: 'Abandoned Calls', value: cc.abandoned },
        { metric: 'Total Talk Time (s)', value: cc.total_talk_time_seconds },
        { metric: 'Credits Spent', value: ccr.spent },
        { metric: 'Credits Added', value: ccr.added },
        { metric: 'Credits Available', value: ccr.available },
      ];
      exportToCSV({
        fileName: `demo-analytics-${companyDetail.company?.name || selectedCompanyId}`,
        data: companyRows,
        columns: [
          { header: 'Metric', accessor: 'metric' },
          { header: 'Value', accessor: 'value' },
        ],
      });
      return;
    }
    if (!data) return;
    const rows = [
      { metric: 'Total Demo Accounts', value: o.total_accounts },
      { metric: 'Active Demo', value: o.active_demo },
      { metric: 'Expired Demo', value: o.expired_demo },
      { metric: 'Deleted Demo', value: o.deleted_demo },
      { metric: 'Converted', value: o.converted },
      { metric: 'Conversion Rate (%)', value: o.conversion_rate },
      { metric: 'Avg Account Age (days)', value: o.avg_account_age_days },
      { metric: 'Total Recharges', value: o.total_recharges },
      { metric: 'Agents', value: e.agents },
      { metric: 'Live Agents', value: e.live_agents },
      { metric: 'Team Leads', value: e.team_leads },
      { metric: 'Call Centers', value: e.call_centres },
      { metric: 'Nested Companies', value: e.nested_companies },
      { metric: 'Total Calls', value: c.total },
      { metric: 'Incoming Calls', value: c.incoming },
      { metric: 'Outgoing Calls', value: c.outgoing },
      { metric: 'Answered Calls', value: c.answered },
      { metric: 'Failed Calls', value: c.failed },
      { metric: 'Abandoned Calls', value: c.abandoned },
      { metric: 'Total Talk Time (s)', value: c.total_talk_time_seconds },
      { metric: 'Credits Spent For Calls', value: cr.spent },
      { metric: 'Credits Added', value: cr.added },
    ];
    exportToCSV({
      fileName: 'overall-demo-conversion-analytics',
      data: rows,
      columns: [
        { header: 'Metric', accessor: 'metric' },
        { header: 'Value', accessor: 'value' },
      ],
    });
  };

  const conversionDonut = {
    options: { labels: ['Converted', 'Active Demo', 'Expired Demo', 'Deleted Demo'], legend: { position: 'bottom' }, colors: [SUCCESS_COLOR, VIOLET_COLOR, FAILURE_COLOR, NEUTRAL_COLOR], dataLabels: { enabled: true } },
    series: [Number(o.converted) || 0, Number(o.active_demo) || 0, Number(o.expired_demo) || 0, Number(o.deleted_demo) || 0],
  };
  const callsDonut = {
    options: { labels: ['Incoming', 'Outgoing'], legend: { position: 'bottom' }, colors: [INCOMING_COLOR, OUTGOING_COLOR], dataLabels: { enabled: true } },
    series: [Number(c.incoming) || 0, Number(c.outgoing) || 0],
  };
  const entitiesBar = {
    options: { chart: { toolbar: { show: false } }, xaxis: { categories: ['Agents', 'Live Agents', 'Team Leads', 'Call Centers', 'Companies'] }, colors: [VIOLET_COLOR], plotOptions: { bar: { borderRadius: 4, distributed: true } }, legend: { show: false }, dataLabels: { enabled: true } },
    series: [{ name: 'Count', data: [Number(e.agents) || 0, Number(e.live_agents) || 0, Number(e.team_leads) || 0, Number(e.call_centres) || 0, Number(e.nested_companies) || 0] }],
  };
  const dailyArea = {
    options: { chart: { toolbar: { show: false }, stacked: false }, xaxis: { categories: daily.map((d) => d.day) }, colors: [INCOMING_COLOR, OUTGOING_COLOR, SUCCESS_COLOR], stroke: { curve: 'smooth', width: 2 }, markers: { size: daily.length <= 1 ? 6 : 0, hover: { size: 7 } }, plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    series: [
      { name: 'Incoming', data: daily.map((d) => d.incoming) },
      { name: 'Outgoing', data: daily.map((d) => d.outgoing) },
      { name: 'Answered', data: daily.map((d) => d.answered) },
    ],
  };
  const conversionsMonthBar = {
    options: { chart: { toolbar: { show: false } }, xaxis: { categories: conv.map((m) => m.month) }, colors: [SUCCESS_COLOR], plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } }, legend: { show: false }, dataLabels: { enabled: true } },
    series: [{ name: 'Conversions', data: conv.map((m) => m.count) }],
  };
  const creditsBar = {
    options: { chart: { toolbar: { show: false } }, xaxis: { categories: ['Spent', 'Added'] }, colors: [FAILURE_COLOR, SUCCESS_COLOR], plotOptions: { bar: { borderRadius: 4, distributed: true, horizontal: true } }, legend: { show: false }, dataLabels: { enabled: true } },
    series: [{ name: 'Credits', data: [Number(cr.spent) || 0, Number(cr.added) || 0] }],
  };
  const companyCallsDonut = {
    options: { labels: ['Incoming', 'Outgoing'], legend: { position: 'bottom' }, colors: [INCOMING_COLOR, OUTGOING_COLOR], dataLabels: { enabled: true } },
    series: [Number(cdCalls.incoming) || 0, Number(cdCalls.outgoing) || 0],
  };
  const companyDailyArea = {
    options: { chart: { toolbar: { show: false }, stacked: false }, xaxis: { categories: cdDaily.map((d) => d.day) }, colors: [INCOMING_COLOR, OUTGOING_COLOR, SUCCESS_COLOR], stroke: { curve: 'smooth', width: 2 }, markers: { size: cdDaily.length <= 1 ? 6 : 0, hover: { size: 7 } }, plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    series: [
      { name: 'Incoming', data: cdDaily.map((d) => d.incoming) },
      { name: 'Outgoing', data: cdDaily.map((d) => d.outgoing) },
      { name: 'Answered', data: cdDaily.map((d) => d.answered) },
    ],
  };

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">Overall Demo Conversion Analytics</span>
        <button
          type="button"
          title="Refresh"
          onClick={refetching ? undefined : handleRefetch}
          disabled={refetching}
          className={
            'flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors ' +
            (refetching ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted hover:text-foreground')
          }
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (refetching ? 'animate-spin' : '')} />
        </button>
      </div>

      {/* Filters */}
      <DateRangeFilter
        actionsAtEnd
        compact
        className="mb-3"
        value={{ from: startDate, to: endDate }}
        min={dateBounds.minFrom}
        max={dateBounds.today}
        onChange={({ from, to }) => {
          if (from !== startDate) {
            if (from && from < dateBounds.minFrom) {
              toast.info('From date cannot be earlier than 3 months ago');
              setStartDate(dateBounds.minFrom);
            } else setStartDate(from);
          }
          if (to !== endDate) setEndDate(to);
        }}
        onApply={handleApply}
        applyLabel="Apply"
        onReset={handleReset}
        actions={
          <Button className="h-9" onClick={handleExport} disabled={selectedCompanyId ? !companyDetail : !data}>
            <Download className="mr-1.5 h-3.5 w-3.5" />Export
          </Button>
        }
      >
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Demo Company</label>
          <select
            value={selectedCompanyId}
            onChange={(e2) => setSelectedCompanyId(e2.target.value)}
            className="h-9 w-[240px] rounded-md border border-border bg-background px-2 text-sm"
            disabled={companyOptions.length === 0}
          >
            <option value="">{companyOptions.length === 0 ? 'No demo companies' : 'All (overall)'}</option>
            {companyOptions.map((co) => (
              <option key={co.id} value={co.id}>
                {co.name}{co.deleted ? ' (Deleted)' : co.converted ? ' (converted)' : ''}
              </option>
            ))}
          </select>
        </div>
      </DateRangeFilter>

      {selectedCompanyId ? null : loading ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Loading analytics…</div>
      ) : !data ? (
        <div className="p-6 text-center text-sm text-muted-foreground">No data available.</div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Total Demo Accounts" value={o.total_accounts} color="#6366f1" />
            <StatCard label="Active Demo" value={o.active_demo} color="#0ea5e9" />
            <StatCard label="Expired Demo" value={o.expired_demo} color="#ef4444" />
            <StatCard label="Deleted Demo" value={o.deleted_demo ?? 0} color="#64748b" />
            <StatCard label="Converted" value={o.converted} color="#22c55e" />
            <StatCard label="Conversion Rate" value={`${o.conversion_rate ?? 0}%`} color="#a855f7" />
            <StatCard label="Avg Account Age" value={`${o.avg_account_age_days ?? 0} days`} color="#14b8a6" />
            <StatCard label="Total Recharges" value={o.total_recharges} color="#eab308" />
            <StatCard label="Credits Spent For Calls" value={cr.spent} color="#dc3545" />
            <StatCard label="Total Calls" value={c.total} color="#0ea5e9" />
            <StatCard label="Total Talk Time" value={fmtTalkTime(c.total_talk_time_seconds)} color="#10b981" />
            <StatCard label="Agents" value={e.agents} color="#6366f1" />
            <StatCard label="Call Centers" value={e.call_centres} color="#a855f7" />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ChartPanel title="Converted vs Active vs Expired vs Deleted Demo">
              <Chart options={conversionDonut.options} series={conversionDonut.series} type="donut" height={260} />
            </ChartPanel>
            <ChartPanel title="Incoming vs Outgoing">
              <Chart options={callsDonut.options} series={callsDonut.series} type="donut" height={260} />
            </ChartPanel>
            <ChartPanel title="Entities Created">
              <Chart options={entitiesBar.options} series={entitiesBar.series} type="bar" height={280} />
            </ChartPanel>
            <ChartPanel title="Conversions Over Time">
              {conv.length ? (
                <Chart options={conversionsMonthBar.options} series={conversionsMonthBar.series} type="bar" height={280} />
              ) : <div className="py-6 text-center text-sm text-muted-foreground">No conversions yet.</div>}
            </ChartPanel>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <ChartPanel title="Daily Calls">
              {daily.length ? (
                <Chart options={dailyArea.options} series={dailyArea.series} type={daily.length <= 1 ? 'bar' : 'area'} height={300} />
              ) : <div className="py-6 text-center text-sm text-muted-foreground">No call activity in this range.</div>}
            </ChartPanel>
            <ChartPanel title="Credits">
              <Chart options={creditsBar.options} series={creditsBar.series} type="bar" height={220} />
            </ChartPanel>
            <ChartPanel title="Recent Demo Conversions">
              {recent.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr className="bg-muted/50 text-left text-xs text-muted-foreground">
                        <th className="px-3 py-2.5 font-bold">#</th>
                        <th className="px-3 py-2.5 font-bold">Company</th>
                        <th className="px-3 py-2.5 font-bold">Converted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((r, i) => (
                        <tr key={r.id} className="border-t border-border">
                          <td className="px-3 py-2.5">{i + 1}</td>
                          <td className="px-3 py-2.5">{r.name || '—'}</td>
                          <td className="px-3 py-2.5">{r.demo_converted_at ? new Date(r.demo_converted_at).toLocaleString('en-IN') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="py-6 text-center text-sm text-muted-foreground">No conversions yet.</div>}
            </ChartPanel>
          </div>
        </>
      )}

      {selectedCompanyId ? (
        <div className="mt-4">
          <h6 className="mb-3 font-bold text-foreground">
            {selectedCompanyName || 'Selected Company'} — Demo Details
            {cd?.from_snapshot ? (
              <span className="ml-2 text-xs font-normal opacity-75">(captured snapshot at conversion — date range not applied)</span>
            ) : null}
          </h6>
          {companyLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading company analytics…</div>
          ) : !cd ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No data available for this company.</div>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard label="Account Age" value={`${cdOverview.account_age_days ?? 0} days`} color="#6366f1" />
                <StatCard label="Days Used" value={`${cdOverview.days_used ?? 0} days`} color="#0ea5e9" />
                <StatCard label="Recharges" value={cdOverview.recharge_count ?? 0} color="#a855f7" />
                <StatCard
                  label={cdOverview.deleted || selectedCompanyMeta?.deleted ? 'Deleted At' : cdOverview.converted ? 'Converted On' : 'Status'}
                  value={
                    cdOverview.deleted || selectedCompanyMeta?.deleted
                      ? (cdOverview.deleted_at || selectedCompanyMeta?.deleted_at)
                        ? new Date(cdOverview.deleted_at || selectedCompanyMeta.deleted_at).toLocaleString('en-IN')
                        : 'Deleted'
                      : cdOverview.converted
                        ? new Date(cdOverview.conversion_date).toLocaleDateString('en-IN')
                        : cdOverview.expired ? 'Expired Demo' : 'Active Demo'
                  }
                  color="#f97316"
                />
                <StatCard label="Total Calls" value={cdCalls.total ?? 0} color="#14b8a6" />
                <StatCard label="Answered" value={cdCalls.answered ?? 0} color="#10b981" />
                <StatCard label="Failed" value={cdCalls.failed ?? 0} color="#ef4444" />
                <StatCard label="Abandoned" value={cdCalls.abandoned ?? 0} color="#f59e0b" />
                <StatCard label="Talk Time" value={fmtTalkTime(cdCalls.total_talk_time_seconds)} color="#22c55e" />
                <StatCard label="Credits Spent" value={cdCredits.spent ?? 0} color="#dc3545" />
                <StatCard label="Credits Added" value={cdCredits.added ?? 0} color="#28a745" />
                <StatCard label="Credits Available" value={cdCredits.available ?? 0} color="#007bff" />
                <StatCard label="Agents" value={cdEntities.agents ?? 0} color="#6366f1" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <ChartPanel title="Incoming vs Outgoing">
                  {(Number(cdCalls.incoming) || 0) + (Number(cdCalls.outgoing) || 0) > 0 ? (
                    <Chart options={companyCallsDonut.options} series={companyCallsDonut.series} type="donut" height={260} />
                  ) : <div className="py-6 text-center text-sm text-muted-foreground">No calls in this range.</div>}
                </ChartPanel>
                <ChartPanel title="Entities Created">
                  <table className="w-full text-[13px]">
                    <tbody>
                      <tr className="border-b border-border"><td className="py-1.5">Agents</td><td className="py-1.5 text-right font-bold">{cdEntities.agents ?? 0}</td></tr>
                      <tr className="border-b border-border"><td className="py-1.5">Team Leads</td><td className="py-1.5 text-right font-bold">{cdEntities.team_leads ?? 0}</td></tr>
                      <tr className="border-b border-border"><td className="py-1.5">Call Centers</td><td className="py-1.5 text-right font-bold">{cdEntities.call_centres ?? 0}</td></tr>
                      <tr><td className="py-1.5">Nested Companies</td><td className="py-1.5 text-right font-bold">{cdEntities.nested_companies ?? 0}</td></tr>
                    </tbody>
                  </table>
                </ChartPanel>
              </div>
              <div className="mt-3">
                <ChartPanel title="Daily Calls">
                  {cdDaily.length ? (
                    <Chart options={companyDailyArea.options} series={companyDailyArea.series} type={cdDaily.length <= 1 ? 'bar' : 'area'} height={280} />
                  ) : <div className="py-6 text-center text-sm text-muted-foreground">No call activity in this range.</div>}
                </ChartPanel>
              </div>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}
